import React from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console (in production, you'd send this to an error reporting service)
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // In production, you would send this to an error reporting service like Sentry
    if (window.location.hostname !== 'localhost') {
      // Example: Send to error reporting service
      // errorReportingService.logError(error, errorInfo, this.props.context);
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div className="min-h-[400px] flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertTriangle" size={32} className="text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Something went wrong
              </h2>
              <p className="text-gray-600 mb-4">
                {this.props.fallbackMessage || 
                 "We encountered an unexpected error. Please try again or reload the page."}
              </p>
              
              {/* Show error details in development */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="text-left bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <summary className="cursor-pointer font-medium text-red-800 mb-2">
                    Error Details (Development)
                  </summary>
                  <div className="text-sm text-red-700">
                    <div className="font-medium mb-1">Error:</div>
                    <div className="mb-2 font-mono text-xs bg-red-100 p-2 rounded">
                      {this.state.error.toString()}
                    </div>
                    <div className="font-medium mb-1">Stack Trace:</div>
                    <pre className="text-xs bg-red-100 p-2 rounded whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </pre>
            </div>
                </details>
              )}
            </div>
            <div className="space-y-2">
              <Button 
                onClick={this.handleRetry} 
                variant="primary"
                className="w-full"
              >
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Try Again
              </Button>
              
              {this.state.retryCount > 0 && (
                <Button 
                  onClick={this.handleReload} 
                  variant="outline"
                  className="w-full"
              >
                  <Icon name="RotateCcw" size={16} className="mr-2" />
                  Reload Page
                </Button>
              )}
            </div>

            {this.state.retryCount > 2 && (
              <div className="mt-4 text-sm text-gray-500">
                Having trouble? Try refreshing the page or contact support if the problem persists.
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;